/**
 * Plan Wizard Component
 * Multi-step wizard for creating waste management plans
 * Uses new two-phase AI workflow: ProjectFacts -> WasteAnalysis -> Review
 * Supports both authenticated and guest (unauthenticated) modes
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ProjectInfo, ApplicantInfo, PlanData } from '../../types';
import { CountySelectionStep } from './CountySelectionStep';
import { ProjectFactsStep, type ProjectFactsData } from './ProjectFactsStep';
import { WasteAnalysisStep, type WasteAnalysisData } from './WasteAnalysisStep';
import { ReviewStep } from './ReviewStep';
import { calculateCompliance } from '../../utils/calgreen';
import { guestPlanService } from '../../services/guest-plan.service';
import { toast } from '@/lib/utils/toast';

interface PlanWizardProps {
  initialData?: Partial<PlanData>;
  onSubmit: (planData: PlanData) => Promise<void>;
  existingPlanId?: string;
  isSubmitting?: boolean;
  countyId?: string;
}

/**
 * Convert ProjectFacts to legacy ProjectInfo format
 */
function convertFactsToProjectInfo(facts: ProjectFactsData): ProjectInfo {
  return {
    project_name: facts.projectName,
    project_address: facts.projectAddress,
    project_type: facts.projectType as ProjectInfo['project_type'],
    estimated_start_date: facts.startDate,
    estimated_completion_date: facts.completionDate,
    total_square_footage: facts.squareFootage ? parseInt(facts.squareFootage) : undefined,
  };
}

/**
 * Convert ProjectFacts to legacy ApplicantInfo format
 */
function convertFactsToApplicantInfo(facts: ProjectFactsData): ApplicantInfo {
  return {
    contact_name: facts.contactName,
    contact_email: facts.contactEmail,
    contact_phone: facts.contactPhone,
    company_name: facts.companyName || undefined,
  };
}

export function PlanWizard({ initialData, onSubmit, existingPlanId, isSubmitting: externalIsSubmitting = false, countyId }: PlanWizardProps) {
  const navigate = useNavigate();
  // New flow: -1 = county, 0 = facts, 1 = analysis, 2 = review
  // Start at step -1 (county selection) if no countyId provided, otherwise step 0 (facts)
  const [currentStep, setCurrentStep] = useState(initialData ? 0 : (countyId ? 0 : -1));
  const [internalIsSubmitting, setInternalIsSubmitting] = useState(false);
  const [planId, setPlanId] = useState<string | null>(existingPlanId || null);

  const isSubmitting = externalIsSubmitting || internalIsSubmitting;

  // County selection state
  const [selectedCounty, setSelectedCounty] = useState<{
    id: string;
    name: string;
  } | null>(countyId ? { id: countyId, name: '' } : null);

  // New state structure for two-phase flow (single source of truth)
  const [projectFacts, setProjectFacts] = useState<ProjectFactsData | null>(null);
  const [wasteAnalysis, setWasteAnalysis] = useState<WasteAnalysisData | null>(null);

  // Auto-save draft using guest plan service
  useEffect(() => {
    if (projectFacts || wasteAnalysis) {
      guestPlanService.saveDraft({
        county: selectedCounty || undefined,
        facts: projectFacts || undefined,
        analysis: wasteAnalysis || undefined,
      });
    }
  }, [projectFacts, wasteAnalysis, selectedCounty]);

  const handleCountySelection = (countyId: string, countyName: string) => {
    setSelectedCounty({ id: countyId, name: countyName });
    setCurrentStep(0); // Move to Project Facts step
  };

  const handleProjectFactsNext = (data: ProjectFactsData) => {
    setProjectFacts(data);

    // Generate unique ID and update URL if not already set
    if (!planId) {
      const newId = crypto.randomUUID();
      setPlanId(newId);
      navigate(`/plans/${newId}/create`, { replace: true });
    }

    setCurrentStep(1); // Move to Waste Analysis step
  };

  const handleWasteAnalysisNext = (data: WasteAnalysisData) => {
    setWasteAnalysis(data);
    setCurrentStep(2); // Move to Review step
  };

  const handleFinalSubmit = async () => {
    if (!projectFacts || !wasteAnalysis) {
      console.error('Missing project facts or waste analysis');
      return;
    }

    setInternalIsSubmitting(true);

    try {
      // Convert to legacy format at submit time
      const projectInfo = convertFactsToProjectInfo(projectFacts);
      const applicantInfo = convertFactsToApplicantInfo(projectFacts);
      const wasteStreams = wasteAnalysis.wasteStreams;

      const compliance = calculateCompliance(wasteStreams);

      const planData: PlanData = {
        project_info: projectInfo,
        applicant_info: applicantInfo,
        waste_streams: wasteStreams,
        compliance,
        operational_requirements: wasteAnalysis.operationalRequirements,
        version: '1.0',
      };

      await onSubmit(planData);

      // Clear the draft after successful submission
      guestPlanService.clearDraft();
    } catch (error) {
      console.error('Failed to submit plan:', error);
      toast.error('Failed to submit plan. Please try again.');
    } finally {
      setInternalIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (confirm('Are you sure you want to cancel? Your progress is auto-saved locally.')) {
      navigate('/');
    }
  };

  // Define steps for new two-phase flow
  const steps = [
    { number: 1, name: 'Project Details' },
    { number: 2, name: 'Waste Analysis' },
    { number: 3, name: 'Review' },
  ];

  return (
    <div className="max-w-4xl mx-auto">
      {/* Auto-save Banner - only show when past county selection and quick start */}
      {currentStep > 0 && (
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-900">
                Your Progress is Auto-Saved
              </p>
              <p className="text-sm text-blue-700 mt-1">
                Your plan is automatically saved as you work. Once created, you&apos;ll receive a unique URL that you can save and share to access your plan.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Progress Indicator - only show when past county selection and quick start */}
      {currentStep > 0 && (
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <React.Fragment key={step.number}>
                <div className="flex flex-col items-center flex-1">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors ${
                      currentStep >= step.number
                        ? 'bg-primary-600 text-white'
                        : 'bg-neutral-200 text-neutral-600'
                    }`}
                    aria-current={currentStep === step.number ? 'step' : undefined}
                  >
                    {step.number}
                  </div>
                  <div
                    className={`mt-2 text-sm font-medium ${
                      currentStep >= step.number ? 'text-primary-600' : 'text-neutral-600'
                    }`}
                  >
                    {step.name}
                  </div>
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`flex-1 h-1 mx-4 transition-colors ${
                      currentStep > step.number ? 'bg-primary-600' : 'bg-neutral-200'
                    }`}
                    aria-hidden="true"
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {/* Step Content */}
      <div className="card">
        {currentStep === -1 && (
          <CountySelectionStep
            onCountySelected={handleCountySelection}
            initialCountyId={selectedCounty?.id}
          />
        )}

        {currentStep === 0 && (
          <ProjectFactsStep
            initialData={projectFacts || undefined}
            onNext={handleProjectFactsNext}
            onCancel={handleCancel}
          />
        )}

        {currentStep === 1 && projectFacts && (
          <WasteAnalysisStep
            projectFacts={projectFacts}
            countyId={selectedCounty?.id || countyId}
            initialData={wasteAnalysis || undefined}
            onNext={handleWasteAnalysisNext}
            onBack={() => setCurrentStep(0)}
          />
        )}

        {currentStep === 2 && projectFacts && wasteAnalysis && (
          <ReviewStep
            projectInfo={convertFactsToProjectInfo(projectFacts)}
            applicantInfo={convertFactsToApplicantInfo(projectFacts)}
            wasteStreams={wasteAnalysis.wasteStreams}
            operationalRequirements={wasteAnalysis.operationalRequirements}
            improvementSuggestions={wasteAnalysis.improvementSuggestions}
            onSubmit={handleFinalSubmit}
            onBack={() => setCurrentStep(1)}
            isSubmitting={isSubmitting}
            onWasteStreamsChange={(newWasteStreams) => {
              setWasteAnalysis({
                ...wasteAnalysis,
                wasteStreams: newWasteStreams,
              });
            }}
          />
        )}
      </div>

    </div>
  );
}